import tailwindUrl from 'tailwindcss/tailwind.css?url';
import appCssUrl from 'app.css?url';
import { webComponentAssetUrl } from './stage';
import { userSession } from 'state/session';
import { brandSchema } from 'data/schema';

const TOAST_HOST_TAG = 'e3d-toast-portal';
const TOAST_RENDER_TARGET_ID = 'toast-render-target';

let shadowContainer: ShadowRoot | null = null;
let brandChangeCleanup: (() => void) | null = null;

/**
 * Injects stylesheets into a shadow root.
 * Returns a promise that resolves when all stylesheets are loaded.
 */
function injectStylesheets(shadow: ShadowRoot): Promise<void[]> {
  const stylesheetUrls = [webComponentAssetUrl(tailwindUrl), webComponentAssetUrl(appCssUrl)];

  const loadPromises = stylesheetUrls.map((href) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    shadow.appendChild(link);

    return new Promise<void>((resolve, reject) => {
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    });
  });

  return Promise.all(loadPromises);
}

/**
 * Copies theme styles from the main document into the shadow root.
 */
function copyThemeStyles(shadow: ShadowRoot): void {
  const globalStyle = document.querySelector('#e3d-theme');
  if (globalStyle) {
    shadow.appendChild(globalStyle.cloneNode(true));
  }
}

/**
 * Creates the render target element inside the shadow DOM.
 */
function createRenderTarget(): HTMLDivElement {
  const renderTarget = document.createElement('div');
  renderTarget.id = TOAST_RENDER_TARGET_ID;

  // Apply initial brand class
  const brand = userSession().get('brand');
  if (brand) {
    renderTarget.classList.add(`brand-${brand}`);
  }

  return renderTarget;
}

/**
 * Sets up brand change listener to update the render target's brand class.
 * Returns a cleanup function to remove the event listener.
 */
function setupBrandChangeListener(renderTarget: HTMLDivElement): () => void {
  const handleBrandChange = (keys: string[], newValues: unknown[]) => {
    const brandIndex = keys.indexOf('brand');
    if (brandIndex === -1) return;

    const newBrand = newValues[brandIndex] as string;
    const inactiveBrands = brandSchema.options.filter((b) => b !== newBrand);

    // Remove all inactive brand classes
    inactiveBrands.forEach((brand) => {
      renderTarget.classList.remove(`brand-${brand}`);
    });

    // Add new brand class
    if (newBrand) {
      renderTarget.classList.add(`brand-${newBrand}`);
    }
  };

  const offChangeListener = userSession().on('change', handleBrandChange);

  // Return cleanup function
  return () => {
    offChangeListener();
  };
}

/**
 * Creates the shadow DOM host element with proper positioning.
 */
function createHostElement(): HTMLElement {
  const host = document.createElement(TOAST_HOST_TAG);
  // Position the host at viewport top-right, zero-size, highest z-index
  // z-index: 2147483647 is the maximum 32-bit signed integer value, ensuring toasts appear above all other content
  // pointer-events: none allows clicks to pass through to elements below
  host.style.cssText =
    'position: fixed; top: 0; right: 0; width: 0; height: 0; z-index: 2147483647; pointer-events: none;';
  return host;
}

/**
 * Gets or creates an isolated shadow DOM container for the toast.
 * Uses shadow DOM to prevent style conflicts with the host page.
 *
 * This is designed to escape any CSS containment from web component
 * containers while maintaining complete style isolation from the
 * integrator's page styles.
 */
export function getToastPortalContainer(): ShadowRoot {
  if (shadowContainer) {
    return shadowContainer;
  }

  // Check for existing host element
  let host = document.querySelector(TOAST_HOST_TAG) as HTMLElement | null;

  if (!host) {
    host = createHostElement();
    document.body.appendChild(host);
  }

  // Get existing or create new shadow root
  shadowContainer = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

  // Inject stylesheets (fire and forget - toast will render after styles load via React)
  injectStylesheets(shadowContainer);
  copyThemeStyles(shadowContainer);

  // Create and configure render target
  const renderTarget = createRenderTarget();
  brandChangeCleanup = setupBrandChangeListener(renderTarget);
  shadowContainer.appendChild(renderTarget);

  return shadowContainer;
}

/**
 * Gets the render target element inside the toast portal's shadow DOM.
 * This is the element that React should portal the toast content into.
 */
export function getToastRenderTarget(): HTMLElement {
  const shadow = getToastPortalContainer();
  const target = shadow.getElementById(TOAST_RENDER_TARGET_ID);

  if (!target) {
    throw new Error('Toast render target not found. This should not happen.');
  }

  return target;
}

/**
 * Cleans up the toast portal. Useful for testing.
 */
export function destroyToastPortal(): void {
  // Clean up event listeners to prevent memory leaks
  if (brandChangeCleanup) {
    brandChangeCleanup();
    brandChangeCleanup = null;
  }

  const host = document.querySelector(TOAST_HOST_TAG);
  if (host) {
    host.remove();
  }
  shadowContainer = null;
}
